import { Tile, TileStatus } from "./Tile";
import { Button } from "@/components/ui/button";
import { Eye } from "lucide-react";

export interface GameRow {
  letters: string[];
  results: TileStatus[];
}

interface GameGridProps {
  rows: GameRow[];
  currentGuess: string;
  currentRowIndex: number;
  solution?: string;
  showSolution?: boolean;
  showRevealButton?: boolean;
  onRevealClick?: () => void;
  revealedPositions?: Record<number, string>;
}

export function GameGrid({ rows, currentGuess, currentRowIndex, solution, showSolution, showRevealButton, onRevealClick, revealedPositions }: GameGridProps) {
  const renderRow = (rowData: GameRow | null, rowIndex: number) => {
    const letters = new Array(5).fill("");
    const statuses: TileStatus[] = new Array(5).fill("empty");
    const isCurrentRow = rowIndex === currentRowIndex;

    if (rowData) {
      // Completed row
      for (let i = 0; i < 5; i++) {
        letters[i] = rowData.letters[i] || "";
        statuses[i] = rowData.results[i] || "empty";
      }
    } else if (isCurrentRow) {
      // Current row being typed
      for (let i = 0; i < 5; i++) {
        letters[i] = currentGuess[i] || "";
        statuses[i] = currentGuess[i] ? "pending" : "empty";
      }
    }

    return (
      <div key={rowIndex} className="flex gap-1.5 sm:gap-2 justify-center">
        {letters.map((letter, colIndex) => (
          <Tile
            key={`${rowIndex}-${colIndex}`}
            char={letter}
            status={statuses[colIndex]}
            delay={colIndex * 150}
            isHinted={isCurrentRow && revealedPositions?.[colIndex] !== undefined}
          />
        ))}
      </div>
    );
  };

  return (
    <div 
      role="grid" 
      aria-label="Game board showing your word guesses"
      data-game-grid="true"
      className="flex flex-col gap-1.5 sm:gap-2 md:landscape:gap-1.5"
    >
      {Array.from({ length: 6 }, (_, i) => renderRow(rows[i] || null, i))}
      
      {/* Show reveal button when auto-reveal is off */}
      {showRevealButton && onRevealClick && (
        <div className="flex justify-center mt-2 pt-2 border-t border-muted">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={onRevealClick}
            className="gap-2"
          >
            <Eye className="h-4 w-4" />
            Reveal Answer
          </Button>
        </div>
      )}
      
      {/* Show solution row for lost games */}
      {showSolution && solution && (
        <div className="flex gap-1.5 sm:gap-2 justify-center mt-2 pt-2 border-t border-muted">
          {solution.split('').map((letter, i) => (
            <Tile key={`solution-${i}`} char={letter} status="correct" delay={i * 150} />
          ))}
        </div>
      )}
    </div>
  );
}